/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow
import type { Node } from 'react';

import React, { useState } from 'react';
// eslint-disable-next-line import/extensions
import { FaChevronDown, FaInfoCircle } from 'react-icons/fa';
import { Button, Collapse } from 'reactstrap';
import { chip } from '#components/pages/Models/Model/support/form/ModelFormField/AiFieldMarking/AiFieldMarking.module.scss';
import {
  legend,
  summary,
  summaryText,
  toggle,
  chevron,
  chevronOpen,
  body,
  inlineIcon,
} from './Sp3AiLegend.module.scss';

type Props = {|
  visible: boolean,
|};

// The reader's open/closed choice, kept per browser. Storage can be unavailable (private
// windows, blocked site data), so every access is guarded and falls back to collapsed.
const STORAGE_KEY = 'sp3AiLegendExpanded';

const readExpanded = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeExpanded = (expanded: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(expanded));
  } catch {
    // Not remembering the choice is harmless.
  }
};

/*
 * An explainer at the top of the first section once SP3-AI-ReportReader has filled in the
 * model. One line by default (what the SP3-AI tag means), with "How to check them" expanding
 * how to trace a value back to the report. Shown only while the form carries SP3-AI markings,
 * proposed or accepted.
 */
const Sp3AiLegend = ({ visible }: Props): Node => {
  const [expanded, setExpanded] = useState(readExpanded);

  if (!visible) {
    return null;
  }

  const handleToggle = () => {
    setExpanded(current => {
      writeExpanded(!current);
      return !current;
    });
  };

  return (
    <aside className={`${legend} sp3-ai-legend`} aria-label="About SP3-AI inputs">
      <div className={summary}>
        <span className={chip}>SP3-AI</span>
        <span className={summaryText}>
          These inputs were read from your structural report by SP3-AI-<em>ReportReader</em>.
        </span>
        <Button
          color="link"
          size="sm"
          className={toggle}
          aria-expanded={expanded}
          aria-controls="sp3AiLegendDetails"
          onClick={handleToggle}
        >
          How to check them
          <FaChevronDown className={expanded ? `${chevron} ${chevronOpen}` : chevron} />
        </Button>
      </div>
      <Collapse isOpen={expanded} id="sp3AiLegendDetails">
        <p className={body}>
          Select the <FaInfoCircle className={inlineIcon} aria-label="info" /> in any SP3-AI tag to
          see the passage the value came from and, where the report states it, open the report at
          that page. Accept the inputs that look right; reject any you want to enter yourself.
        </p>
      </Collapse>
    </aside>
  );
};

export default Sp3AiLegend;
